import {
  CreateParams,
  CreateResult,
  DataProvider,
  DeleteParams,
  GetListResult,
  GetManyParams,
  GetManyResult,
  GetOneParams,
  GetOneResult,
  Identifier,
  UpdateParams,
  UpdateResult,
} from 'react-admin'
import { DEVICE_MANAGEMENT_ITEMS_URL } from '../../../api-urls'
import {
  get,
  getWithPagination,
  patch,
  post,
  remove,
} from '../../../common/fetch.utils'
import {
  filterParamsComposer,
  queryParamsComposer,
} from '../../../common/get-by-conditions.utils'
import { TicketAPIGetListParams } from '../../../common/ticket-api-get-list.params'
import { DeviceManagementItemDto } from '../../../dto/device/device-management/device-management-items/DeviceManagementItem.dto'
import { mapSortItemParam } from './device-management-item-sort.mapper'
import filterMapper from './device-management-item-filter.mapper'

const provider = {
  getList: async (
    resource: string,
    { filter, sort, pagination }: TicketAPIGetListParams,
  ): Promise<GetListResult<DeviceManagementItemDto>> => {
    const filterParams = `c=>${filterParamsComposer('c', filter, filterMapper)}`
    const pathParams = queryParamsComposer(sort, pagination, mapSortItemParam)
    const path = `/${filterParams}${(pathParams && `?${pathParams}`) || ''}`

    const {
      data,
      range: { total },
    } = await getWithPagination<DeviceManagementItemDto[]>(
      `${DEVICE_MANAGEMENT_ITEMS_URL}/GetByConditions`,
      path,
    )

    return Promise.resolve({
      data,
      total,
    })
  },
  getOne: async (
    resource: string,
    { id }: GetOneParams,
  ): Promise<GetOneResult<DeviceManagementItemDto>> => {
    const data = await get<DeviceManagementItemDto>(
      DEVICE_MANAGEMENT_ITEMS_URL,
      `/${id}`,
    )
    return Promise.resolve({
      data,
    })
  },
  getMany: async (
    resource: string,
    { ids }: GetManyParams,
  ): Promise<GetManyResult<DeviceManagementItemDto>> => {
    const data = await get<DeviceManagementItemDto[]>(
      `${DEVICE_MANAGEMENT_ITEMS_URL}/GetByConditions`,
      `/u=>new int[] {${ids.toString()}}.Contains(u.Id)`,
    )
    return Promise.resolve({
      data,
    })
  },
  update: async (
    resource: string,
    { id, data, previousData }: UpdateParams<DeviceManagementItemDto>,
  ): Promise<UpdateResult> => {
    const update = await patch<
      { id: Identifier; [n: string]: any },
      DeviceManagementItemDto
    >(DEVICE_MANAGEMENT_ITEMS_URL, {
      id,
      xPosition:
        data.xPosition !== previousData.xPosition ? data.xPosition : undefined,
      yPosition:
        data.yPosition !== previousData.yPosition ? data.yPosition : undefined,
      deviceType:
        data.deviceType !== previousData.deviceType
          ? data.deviceType
          : undefined,
      deviceId:
        data.deviceId !== previousData.deviceId ? data.deviceId : undefined,
      containerId:
        data.containerId !== previousData.containerId
          ? data.containerId
          : undefined,
    })
    return Promise.resolve({ data: update })
  },
  create: async (
    resource: string,
    { data }: CreateParams<CreateContainerRequest>,
  ): Promise<CreateResult<DeviceManagementItemDto>> => {
    const container = await post<
      CreateContainerRequest[],
      DeviceManagementItemDto[]
    >(DEVICE_MANAGEMENT_ITEMS_URL, [data])
    return {
      data: container[0],
    }
  },
  delete: async (
    resource: string,
    { id }: DeleteParams,
  ): Promise<CreateResult<DeviceManagementItemDto>> => {
    const deleted = await remove<DeviceManagementItemDto>(
      `${DEVICE_MANAGEMENT_ITEMS_URL}/`,
      id as string,
    )
    return {
      data: deleted,
    }
  },
} as DataProvider

export interface CreateContainerRequest {
  readonly xPosition: number
  readonly yPosition: number
  readonly deviceId: number
  readonly containerId: number
}

export default provider
